import { useEffect, useState, useCallback } from 'react'
import { ShoppingCart, X, ListPlus } from 'lucide-react'
import api, { pesanGalat } from '../lib/api'

type Want = {
  id: string; judul: string; artis: string; status: string
  catatan: string | null; dipesan_ke: string | null
  biaya_perkiraan: number | null; song_id: string | null; updated_at: string
}

const PIL: Record<string, string> = {
  baru:      'bg-sky-500/10 text-sky-400 border-sky-500/30',
  dipesan:   'bg-amber-500/10 text-amber-400 border-amber-500/30',
  diabaikan: 'bg-white/5 text-text-3 border-border',
  selesai:   'bg-gold/10 text-gold border-gold/30',
}

const rp = (n: number | null) => (n == null ? '—' : 'Rp ' + n.toLocaleString('id-ID'))

export default function Pesanan({ bolehKelola }: { bolehKelola: boolean }) {
  const [rows, setRows] = useState<Want[]>([])
  const [status, setStatus] = useState('baru')
  const [memuat, setMemuat] = useState(false)
  const [galat, setGalat] = useState('')
  const [pesan, setPesan] = useState<Want | null>(null)
  // 20 Okt 2026: wantlist → entri katalog (judul/artis), berkasnya diisi belakangan.
  const [bahasaBaru, setBahasaBaru] = useState('id')
  const [sumberBaru, setSumberBaru] = useState('lisensi')
  const [kabar, setKabar] = useState('')
  const [sibukMassal, setSibukMassal] = useState(false)

  async function keKatalog(w: Want) {
    setGalat(''); setKabar('')
    try {
      const r = await api.post<{ song_id: string; lagu_baru: boolean }>(`/api/wantlist/${w.id}/katalog`,
        { bahasa: bahasaBaru, sumber: sumberBaru })
      setKabar(r.data.lagu_baru ? `"${w.judul}" dibuat di katalog (draft). Isi berkasnya di Katalog → Siapkan.`
        : `"${w.judul}" sudah ada di katalog — baris ini ditutup.`)
      await muat()
    } catch (e) { setGalat(pesanGalat(e, 'Gagal membuat entri katalog')) }
  }

  async function keKatalogMassal() {
    if (!confirm(`Buat entri katalog (draft) untuk SEMUA lagu berstatus "${status || 'baru'}"? Bahasa ${bahasaBaru}, asal ${sumberBaru}.`)) return
    setGalat(''); setKabar(''); setSibukMassal(true)
    try {
      const r = await api.post<{ dibuat: number; sudah_ada: number; gagal: number; total: number }>(
        '/api/wantlist/katalog-massal', { bahasa: bahasaBaru, sumber: sumberBaru, status: status || 'baru' })
      setKabar(`${r.data.dibuat} entri dibuat, ${r.data.sudah_ada} sudah ada, ${r.data.gagal} gagal (dari ${r.data.total}).`)
      await muat()
    } catch (e) { setGalat(pesanGalat(e, 'Gagal membuat entri massal')) }
    finally { setSibukMassal(false) }
  }

  const muat = useCallback(async () => {
    setMemuat(true); setGalat('')
    try {
      const p = new URLSearchParams()
      if (status) p.set('status', status)
      const r = await api.get(`/api/wantlist?${p}`)
      setRows(r.data ?? [])
    } catch (e) { setGalat(pesanGalat(e, 'Gagal memuat daftar pesanan')) }
    finally { setMemuat(false) }
  }, [status])

  useEffect(() => { muat() }, [muat])

  async function putus(w: Want, patch: Record<string, unknown>) {
    setGalat('')
    try { await api.patch(`/api/wantlist/${w.id}`, patch); await muat() }
    catch (e) { setGalat(pesanGalat(e, 'Gagal menyimpan keputusan')) }
  }

  const totalBiaya = rows.reduce((a, r) => a + (r.biaya_perkiraan ?? 0), 0)

  return (
    <div className="space-y-5">
      <div className="bg-bg-1 border border-border rounded-xl p-4">
        <p className="text-[12.5px] text-text-2 leading-relaxed">
          Lagu yang muncul di chart tapi <b>belum ada di katalog</b>. Ini bahan untuk
          memesan instrumental rekam ulang atau meminta ke penyedia konten.
        </p>
        <p className="text-[11.5px] text-text-3 mt-1.5 leading-relaxed">
          Keputusan di sini <b>bertahan</b> melewati penarikan chart berikutnya — yang
          Anda tandai “diabaikan” tidak akan muncul lagi besok pagi.
        </p>
      </div>

      <div className="bg-bg-1 border border-border rounded-xl p-5">
        <div className="flex items-center gap-2.5 mb-4 flex-wrap">
          <select value={status} onChange={e => setStatus(e.target.value)}>
            <option value="">Semua</option>
            <option value="baru">Baru</option>
            <option value="dipesan">Sudah dipesan</option>
            <option value="diabaikan">Diabaikan</option>
            <option value="selesai">Selesai (masuk katalog)</option>
          </select>
          <span className="text-[12px] text-text-3">{memuat ? 'memuat…' : `${rows.length} lagu`}</span>
          {totalBiaya > 0 && (
            <span className="text-[12px] text-text-2">· perkiraan biaya {rp(totalBiaya)}</span>
          )}
          {bolehKelola && (
            <div className="ml-auto flex items-center gap-1.5 text-[11.5px] text-text-3">
              <select value={bahasaBaru} onChange={e => setBahasaBaru(e.target.value)} className="text-[12px]">
                <option value="id">Indonesia</option><option value="en">Inggris</option>
                <option value="zh">Mandarin</option><option value="jp">Jepang</option><option value="kr">Korea</option>
              </select>
              <select value={sumberBaru} onChange={e => setSumberBaru(e.target.value)} className="text-[12px]">
                <option value="lisensi">lisensi</option><option value="rekam_ulang">rekam ulang</option>
                <option value="milik_sendiri">milik sendiri</option><option value="publik_domain">publik domain</option>
              </select>
              <button onClick={keKatalogMassal} disabled={sibukMassal || rows.length === 0 || status === 'selesai'}
                className="flex items-center gap-1 text-[11.5px] px-2.5 py-1 rounded-md bg-gold/10
                           border border-gold/30 text-gold hover:bg-gold/20 disabled:opacity-40">
                <ListPlus size={12} /> {sibukMassal ? 'Membuat…' : `Buat semua (${rows.length}) di katalog`}
              </button>
            </div>
          )}
        </div>
        {kabar && (
          <div className="text-[12px] text-gold bg-gold/10 border border-gold/25 rounded-lg px-3 py-2 mb-3">{kabar}</div>
        )}

        {galat && (
          <div className="text-[12px] text-red-400 bg-red-500/10 border border-red-500/25 rounded-lg px-3 py-2 mb-3">
            {galat}
          </div>
        )}

        {rows.length === 0 && !memuat ? (
          <div className="text-[13px] text-text-3 py-12 text-center leading-relaxed">
            Belum ada isinya.<br />
            <span className="text-[12px]">Daftar ini terisi otomatis setelah chart berhasil ditarik.</span>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead className="text-[11px] uppercase tracking-wide text-text-3">
                <tr className="border-b border-border">
                  <th className="text-left font-medium py-2">Judul</th>
                  <th className="text-left font-medium py-2">Artis</th>
                  <th className="text-left font-medium py-2 w-28">Status</th>
                  <th className="text-left font-medium py-2 w-40">Dipesan ke</th>
                  <th className="text-left font-medium py-2 w-32">Perkiraan</th>
                  {bolehKelola && <th className="text-left font-medium py-2 w-56">Aksi</th>}
                </tr>
              </thead>
              <tbody>
                {rows.map(w => (
                  <tr key={w.id} className="border-b border-border/60 hover:bg-bg-2/50">
                    <td className="py-2.5 pr-3">{w.judul}</td>
                    <td className="py-2.5 pr-3 text-text-2">{w.artis}</td>
                    <td className="py-2.5">
                      <span className={`text-[11px] px-2 py-0.5 rounded-md border ${PIL[w.status] ?? ''}`}>{w.status}</span>
                    </td>
                    <td className="py-2.5 text-[12px] text-text-3">{w.dipesan_ke ?? '—'}</td>
                    <td className="py-2.5 text-[12px] text-text-2">{rp(w.biaya_perkiraan)}</td>
                    {bolehKelola && (
                      <td className="py-2.5">
                        <div className="flex gap-1.5">
                          {w.status !== 'selesai' && (
                            <button onClick={() => keKatalog(w)} title="Buat entri katalog (draft) — tinggal isi berkasnya"
                              className="flex items-center gap-1 text-[11px] px-2 py-1 rounded-md bg-gold/15
                                         border border-gold/40 text-gold hover:bg-gold/25 transition-colors">
                              <ListPlus size={12} /> Katalog
                            </button>
                          )}
                          {w.status !== 'dipesan' && w.status !== 'selesai' && (
                            <button onClick={() => setPesan(w)}
                              className="flex items-center gap-1 text-[11px] px-2 py-1 rounded-md bg-gold/10
                                         border border-gold/30 text-gold hover:bg-gold/20 transition-colors">
                              <ShoppingCart size={12} /> Pesan
                            </button>
                          )}
                          {w.status !== 'diabaikan' && w.status !== 'selesai' && (
                            <button onClick={() => putus(w, { status: 'diabaikan' })}
                              className="text-[11px] px-2 py-1 rounded-md bg-bg-3 border border-border
                                         text-text-2 hover:border-gold-2 transition-colors">
                              Abaikan
                            </button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {pesan && (
        <DialogPesan w={pesan} tutup={() => setPesan(null)}
          simpan={async (d) => { await putus(pesan, { status: 'dipesan', ...d }); setPesan(null) }} />
      )}
    </div>
  )
}

function DialogPesan({ w, tutup, simpan }: {
  w: Want
  tutup: () => void
  simpan: (d: { dipesan_ke: string; biaya_perkiraan: number | null; catatan: string }) => Promise<void>
}) {
  const [ke, setKe] = useState('')
  const [biaya, setBiaya] = useState('')
  const [catatan, setCatatan] = useState('')
  const [sedang, setSedang] = useState(false)

  return (
    <div className="fixed inset-0 bg-black/70 grid place-items-center p-5 z-50" onClick={tutup}>
      <form onClick={e => e.stopPropagation()}
        onSubmit={async e => {
          e.preventDefault(); setSedang(true)
          await simpan({ dipesan_ke: ke, biaya_perkiraan: biaya ? Number(biaya) : null, catatan })
          setSedang(false)
        }}
        className="w-full max-w-md bg-bg-1 border border-border rounded-2xl p-6 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="font-semibold text-[15px]">Tandai sudah dipesan</h2>
            <p className="text-[12px] text-text-3 mt-0.5">{w.judul} — {w.artis}</p>
          </div>
          <button type="button" onClick={tutup} className="text-text-3 hover:text-text"><X size={18} /></button>
        </div>

        <input required value={ke} onChange={e => setKe(e.target.value)}
          placeholder="Dipesan ke — studio / penyedia" className="w-full" />
        <input type="number" min="0" value={biaya} onChange={e => setBiaya(e.target.value)}
          placeholder="Perkiraan biaya (Rp)" className="w-full" />
        <textarea value={catatan} onChange={e => setCatatan(e.target.value)} rows={2}
          placeholder="Catatan" className="w-full" />

        <div className="flex gap-2 justify-end">
          <button type="button" onClick={tutup}
            className="text-[13px] px-4 py-2 rounded-lg bg-bg-3 border border-border text-text-2">Batal</button>
          <button type="submit" disabled={sedang}
            className="text-[13px] px-4 py-2 rounded-lg bg-gold/15 border border-gold/30 text-gold
                       hover:bg-gold/25 transition-colors disabled:opacity-50">
            {sedang ? 'Menyimpan…' : 'Simpan'}
          </button>
        </div>
      </form>
    </div>
  )
}
